import { useState, useRef, useEffect, useCallback, forwardRef } from 'react'
import { clsx } from 'clsx'
import { coperturaApi } from '@/services/api'
import type { CoperturaComune } from '@/types'
import styles from './SearchableSelect.module.css'

/**
 * Autocomplete per il campo "Comune" che pesca dalla tabella heracomm_comuni
 * via GET /api/v1/copertura-comuni/search.
 *
 * - Funziona in modo controllato: il valore visualizzato e' `value` (passato
 *   da react-hook-form), e ogni selezione invoca `onChange` con un evento
 *   sintetico contenente il nome del comune.
 * - Forza la selezione dal menu: se l'utente digita testo libero che non
 *   corrisponde a nessuna voce della tabella, lo stato interno viene
 *   marcato come "non confermato" e on blur il campo segnala errore.
 * - All'avvenuta selezione, invoca `onSelect(comune)` cosi' il chiamante
 *   puo' auto-popolare CAP, Provincia, Regione, copertura luce/gas, ecc.
 */
interface ComuneAutocompleteProps {
  label: string
  name: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  error?: string
  hint?: string
  icon?: string
  className?: string
  /** valore corrente (nome del comune in maiuscolo) */
  value?: string
  /** callback per react-hook-form: viene invocato con un evento sintetico
   *  (target.value = nome comune) sia quando l'utente digita sia quando
   *  seleziona una voce dal menu. */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  /** invocato a ogni selezione effettiva di un comune del menu (NON quando
   *  l'utente digita testo libero). Utile per auto-popolare CAP/Provincia. */
  onSelect?: (comune: CoperturaComune) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
}

const SEARCH_DEBOUNCE_MS = 220
const MIN_QUERY_CHARS = 2

export const ComuneAutocomplete = forwardRef<HTMLInputElement, ComuneAutocompleteProps>(({
  label,
  name,
  placeholder = 'Inizia a digitare il comune...',
  required = false,
  disabled = false,
  error,
  hint,
  icon = 'building',
  className,
  value,
  onChange,
  onSelect,
  onBlur,
}, ref) => {
  const [isOpen, setIsOpen] = useState(false)
  const [results, setResults] = useState<CoperturaComune[]>([])
  const [loading, setLoading] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [confirmed, setConfirmed] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [mobileSearch, setMobileSearch] = useState('')

  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const internalInputRef = useRef<HTMLInputElement | null>(null)
  const mobileSearchRef = useRef<HTMLInputElement>(null)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastQueryRef = useRef<string>('')

  const inputId = `comune-${name}`

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  // Quando il valore esterno cambia a un valore vuoto, reset stato confermato.
  useEffect(() => {
    if (!value) setConfirmed(false)
  }, [value])

  const search = useCallback(async (query: string) => {
    const trimmed = query.trim()
    if (trimmed.length < MIN_QUERY_CHARS) {
      setResults([])
      setLoading(false)
      return
    }
    lastQueryRef.current = trimmed
    setLoading(true)
    try {
      const data = await coperturaApi.search(trimmed, 12)
      // Ignora la risposta se nel frattempo l'utente ha cambiato query.
      if (lastQueryRef.current !== trimmed) return
      setResults(data)
    } catch {
      if (lastQueryRef.current === trimmed) setResults([])
    } finally {
      if (lastQueryRef.current === trimmed) setLoading(false)
    }
  }, [])

  const debouncedSearch = useCallback((query: string) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => search(query), SEARCH_DEBOUNCE_MS)
  }, [search])

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    }
  }, [])

  const open = useCallback(() => {
    if (disabled) return
    setIsOpen(true)
    setHighlightedIndex(-1)
    if (isMobile) {
      setMobileSearch(value || '')
      document.body.style.overflow = 'hidden'
      // Mostra subito eventuali risultati per il valore corrente
      if (value) debouncedSearch(value)
    } else if (value && value.length >= MIN_QUERY_CHARS) {
      debouncedSearch(value)
    }
  }, [disabled, isMobile, value, debouncedSearch])

  const close = useCallback(() => {
    setIsOpen(false)
    setHighlightedIndex(-1)
    if (isMobile) {
      document.body.style.overflow = ''
      setMobileSearch('')
    }
  }, [isMobile])

  const emitChange = useCallback((next: string) => {
    if (!onChange) return
    const syntheticEvent = {
      target: { value: next, name },
    } as React.ChangeEvent<HTMLInputElement>
    onChange(syntheticEvent)
  }, [onChange, name])

  const selectComune = useCallback((c: CoperturaComune) => {
    emitChange(c.comune)
    setConfirmed(true)
    onSelect?.(c)
    close()
    internalInputRef.current?.focus()
  }, [emitChange, onSelect, close])

  const clearValue = useCallback(() => {
    emitChange('')
    setConfirmed(false)
    setResults([])
  }, [emitChange])

  // chiusura on click outside
  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, close])

  // chiusura on ESC
  useEffect(() => {
    if (!isOpen) return
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [isOpen, close])

  // focus mobile search bar
  useEffect(() => {
    if (isOpen && isMobile && mobileSearchRef.current) {
      setTimeout(() => mobileSearchRef.current?.focus(), 100)
    }
  }, [isOpen, isMobile])

  // scroll highlighted into view
  useEffect(() => {
    if (highlightedIndex >= 0 && itemRefs.current[highlightedIndex]) {
      itemRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightedIndex])

  const handleDesktopInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value
    emitChange(next)
    setConfirmed(false)
    if (!isOpen) setIsOpen(true)
    setHighlightedIndex(-1)
    debouncedSearch(next)
  }

  const handleMobileSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value
    setMobileSearch(next)
    setHighlightedIndex(-1)
    debouncedSearch(next)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      e.preventDefault()
      open()
      return
    }
    if (!isOpen) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex(prev => (prev < results.length - 1 ? prev + 1 : 0))
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : results.length - 1))
        break
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex >= 0 && results[highlightedIndex]) {
          selectComune(results[highlightedIndex])
        }
        break
      case 'Tab':
        close()
        break
    }
  }

  const setRefs = useCallback((el: HTMLInputElement | null) => {
    internalInputRef.current = el
    if (typeof ref === 'function') ref(el)
    else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = el
  }, [ref])

  const wrapperClasses = clsx(
    styles.wrapper,
    { [styles.hasError]: error, [styles.hasIcon]: icon, [styles.disabled]: disabled },
    className
  )

  const queryForHighlight = (isMobile && isOpen ? mobileSearch : (value || '')).trim()

  const highlightMatch = (text: string, query: string) => {
    if (!query) return text
    const idx = text.toLowerCase().indexOf(query.toLowerCase())
    if (idx === -1) return text
    return (
      <>
        {text.slice(0, idx)}
        <mark className={styles.highlight}>{text.slice(idx, idx + query.length)}</mark>
        {text.slice(idx + query.length)}
      </>
    )
  }

  const renderOption = (c: CoperturaComune, idx: number, mode: 'desktop' | 'mobile') => {
    const isSelected = !!value && c.comune.toLowerCase() === value.toLowerCase()
    const optionClass = mode === 'desktop'
      ? clsx(styles.option, {
          [styles.optionHighlighted]: idx === highlightedIndex,
          [styles.optionSelected]: isSelected,
        })
      : clsx(styles.sheetOption, {
          [styles.sheetOptionSelected]: isSelected,
        })

    return (
      <button
        key={`${c.comune}-${c.cap}-${c.provincia}`}
        type="button"
        role="option"
        ref={el => { if (mode === 'desktop') itemRefs.current[idx] = el }}
        aria-selected={isSelected}
        className={optionClass}
        onClick={() => selectComune(c)}
        onMouseEnter={() => mode === 'desktop' && setHighlightedIndex(idx)}
      >
        <span className={styles.optionText}>
          {highlightMatch(c.comune, queryForHighlight)}{' '}
          <small style={{ color: 'var(--color-neutral-500)', fontWeight: 400 }}>
            ({c.provincia}) · {c.cap}
          </small>
        </span>
        {isSelected && (
          mode === 'desktop'
            ? <i className={clsx('bi bi-check-lg', styles.checkIcon)} />
            : <span className={styles.sheetCheck}><i className="bi bi-check-circle-fill" /></span>
        )}
      </button>
    )
  }

  const showNoResults = !loading && results.length === 0
    && (isMobile ? mobileSearch.length >= MIN_QUERY_CHARS : (value || '').length >= MIN_QUERY_CHARS)

  return (
    <div className={wrapperClasses} ref={containerRef}>
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}

      <div className={styles.inputWrapper}>
        {icon && (
          <span className={styles.iconWrapper}>
            <i className={`bi bi-${icon}`} />
          </span>
        )}

        <input
          ref={setRefs}
          id={inputId}
          name={name}
          type="text"
          readOnly={isMobile}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          role="combobox"
          className={clsx(styles.input, { [styles.hasValue]: !!value })}
          value={value || ''}
          onChange={handleDesktopInputChange}
          onFocus={() => { if (!isMobile && !isOpen) open() }}
          onClick={() => { if (isMobile) open() }}
          onKeyDown={handleKeyDown}
          onBlur={(e) => {
            if (!containerRef.current?.contains(e.relatedTarget as Node)) {
              onBlur?.(e)
              if (!isMobile) close()
            }
          }}
        />

        {value && !disabled && (
          <button
            type="button"
            className={styles.clearButton}
            onClick={(e) => { e.stopPropagation(); clearValue() }}
            tabIndex={-1}
            aria-label="Cancella selezione"
          >
            <i className="bi bi-x-lg" />
          </button>
        )}

        <span className={styles.chevron} onClick={() => { if (!disabled) isOpen ? close() : open() }}>
          <i className={clsx('bi', isOpen ? 'bi-chevron-up' : 'bi-chevron-down')} />
        </span>
      </div>

      {/* Desktop dropdown */}
      {isOpen && !isMobile && (
        <div className={styles.dropdown} ref={dropdownRef} role="listbox" aria-label={label}>
          {loading ? (
            <div className={styles.noResults}>
              <i className="bi bi-arrow-repeat" />
              <span>Ricerca in corso...</span>
            </div>
          ) : showNoResults ? (
            <div className={styles.noResults}>
              <i className="bi bi-search" />
              <span>Nessun comune trovato</span>
            </div>
          ) : results.length === 0 ? (
            <div className={styles.noResults}>
              <i className="bi bi-keyboard" />
              <span>Digita almeno {MIN_QUERY_CHARS} caratteri</span>
            </div>
          ) : (
            <div className={styles.optionsList}>
              {results.map((c, idx) => renderOption(c, idx, 'desktop'))}
            </div>
          )}
        </div>
      )}

      {/* Mobile bottom sheet */}
      {isOpen && isMobile && (
        <div className={styles.overlay} onClick={close}>
          <div className={styles.bottomSheet} onClick={e => e.stopPropagation()}>
            <div className={styles.sheetHandle} />
            <div className={styles.sheetHeader}>
              <span className={styles.sheetTitle}>{label}</span>
              <button type="button" className={styles.sheetClose} onClick={close}>
                <i className="bi bi-x-lg" />
              </button>
            </div>

            <div className={styles.sheetSearch}>
              <i className="bi bi-search" />
              <input
                ref={mobileSearchRef}
                type="text"
                className={styles.sheetSearchInput}
                placeholder="Cerca comune..."
                value={mobileSearch}
                onChange={handleMobileSearchChange}
              />
              {mobileSearch && (
                <button
                  type="button"
                  className={styles.sheetSearchClear}
                  onClick={() => { setMobileSearch(''); setResults([]) }}
                >
                  <i className="bi bi-x-lg" />
                </button>
              )}
            </div>

            <div className={styles.sheetList} role="listbox" aria-label={label}>
              {loading ? (
                <div className={styles.noResults}>
                  <i className="bi bi-arrow-repeat" />
                  <span>Ricerca in corso...</span>
                </div>
              ) : showNoResults ? (
                <div className={styles.noResults}>
                  <i className="bi bi-search" />
                  <span>Nessun comune trovato</span>
                </div>
              ) : results.length === 0 ? (
                <div className={styles.noResults}>
                  <i className="bi bi-keyboard" />
                  <span>Digita almeno {MIN_QUERY_CHARS} caratteri</span>
                </div>
              ) : (
                results.map((c, idx) => renderOption(c, idx, 'mobile'))
              )}
            </div>
          </div>
        </div>
      )}

      {error && (
        <p id={`${inputId}-error`} className={styles.error} role="alert">
          <i className="bi bi-exclamation-circle" />
          {error}
        </p>
      )}

      {hint && !error && (
        <p id={`${inputId}-hint`} className={styles.hint}>
          <i className="bi bi-info-circle" />
          {hint}
        </p>
      )}

      {/* Hidden flag esposto via attributo HTML standard per consentire al
          chiamante (in caso di submit forzato) di sapere se il valore e' stato
          confermato dal menu o no. */}
      <span hidden data-comune-confirmed={confirmed ? '1' : '0'} />
    </div>
  )
})

ComuneAutocomplete.displayName = 'ComuneAutocomplete'
